import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackOnboarding } from "@/lib/onboardingTrack";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import MonthDropdown from "@/components/MonthDropdown";
import { Field, Th, Td, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2, ScanLine, Loader2, Sparkles, Wand2 } from "lucide-react";
import MoneyBurst from "@/components/MoneyBurst";
import DateField from "@/components/DateField";

const CATEGORIES = ["Groceries","Dining","Transport","Entertainment","Utilities","Shopping","Healthcare","Education","Travel","Rent","Insurance","Coffee","Other"];

export default function Expenses() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ amount:"", category:"Groceries", description:"", date:new Date().toISOString().slice(0,10), payment_method:"Card", is_essential:true });
  const [burst, setBurst] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanBanner, setScanBanner] = useState(null); // { merchant, currency, confidence } after successful scan
  const [categorizing, setCategorizing] = useState(false);
  const [suggestedCat, setSuggestedCat] = useState(null); // suggestion from description
  const fileInputRef = useRef(null);

  const load = useCallback(async () => { const { data } = await api.get("/expenses"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  // If arrived here via the mobile bottom-bar FAB (which passes
  // `state.autoOpenAdd`), auto-open the Add modal so the primary
  // action is a single tap. The timestamp re-triggers even if the
  // user is already on /expenses.
  const location = useLocation();
  useEffect(() => {
    if (location.state?.autoOpenAdd) {
      setEdit(null);
      setForm({ amount:"", category:"Groceries", description:"", date:new Date().toISOString().slice(0,10), payment_method:"Card", is_essential:true });
      setOpen(true);
    }
  }, [location.state?.autoOpenAdd]);

  const startNew = () => { setEdit(null); setForm({ amount:"", category:"Groceries", description:"", date:new Date().toISOString().slice(0,10), payment_method:"Card", is_essential:true }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ amount:i.amount, category:i.category, description:i.description||"", date:i.date, payment_method:i.payment_method||"Card", is_essential:i.is_essential }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (edit) await api.put(`/expenses/${edit.id}`, payload);
      else await api.post("/expenses", payload);
      // Onboarding: first successful new expense fires the funnel event.
      if (!edit) trackOnboarding("first_expense");
      toast.success(edit ? "Expense updated" : "Expense added");
      setBurst(b => b + 1);
      setTimeout(() => { setOpen(false); load(); }, 350);
    } catch { toast.error(t("common.toast_failed")); }
  };
  const remove = async (id) => { if (!confirm("Delete this expense?")) return; await api.delete(`/expenses/${id}`); toast.success(t("common.toast_deleted")); load(); };

  // One-tap: normalise categories across all expenses by using the winning
  // category for each unique description.
  const autoCategorize = async () => {
    setCategorizing(true);
    try {
      const { data } = await api.post("/expenses/recategorize-all");
      if (data.rows_updated > 0) {
        toast.success(`Normalised ${data.rows_updated} row${data.rows_updated === 1 ? "" : "s"} across ${data.descriptions_seen} descriptions`);
        load();
      } else {
        toast(`Already tidy — ${data.descriptions_seen} unique descriptions, nothing to change.`);
      }
    } catch (e) {
      console.error("Auto-categorize failed:", e);
      toast.error(t("toasts.autocat_failed"));
    } finally { setCategorizing(false); }
  };

  // When user types a description, suggest a category based on past patterns.
  // Only fires when the field loses focus (blur) so we don't spam the endpoint.
  const suggestFromDescription = async (text) => {
    if (!text || text.length < 3 || edit) { setSuggestedCat(null); return; }
    try {
      const { data } = await api.post("/expenses/suggest-category", { text });
      if (data?.category && data.confidence !== "low") {
        setSuggestedCat({ category: data.category, confidence: data.confidence, count: data.match_count });
      } else {
        setSuggestedCat(null);
      }
    } catch (e) { console.debug("expense category suggest failed:", e); }
  };

  const applySuggestion = () => {
    if (!suggestedCat) return;
    setForm(f => ({ ...f, category: suggestedCat.category }));
    setSuggestedCat(null);
  };

  // Receipt-scan flow: user picks an image, we send it to the vision endpoint,
  // pre-fill the "Add expense" form with the extracted fields, and let them
  // review/edit before saving. We never auto-save.
  const onReceiptPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file re-triggers
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error(t("toasts.image_type"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("toasts.image_too_big"));
      return;
    }
    setScanning(true);
    setScanBanner(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/expenses/from-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const CAT_MAP = { Food: "Dining", Transport: "Transport", Utilities: "Utilities",
        Shopping: "Shopping", Entertainment: "Entertainment", Health: "Healthcare",
        Travel: "Travel", Education: "Education", Rent: "Rent", Insurance: "Insurance",
        Other: "Other" };
      setEdit(null);
      setForm({
        amount: data.amount != null ? String(data.amount) : "",
        category: CAT_MAP[data.category] || "Other",
        description: data.merchant || "",
        date: data.date || new Date().toISOString().slice(0, 10),
        payment_method: "Card",
        is_essential: true,
      });
      setScanBanner({
        merchant: data.merchant,
        currency: data.currency,
        confidence: data.confidence,
      });
      setOpen(true);
      toast.success(
        data.confidence === "high" ? "Receipt read — review & save"
        : data.confidence === "medium" ? "Read partial info — please check"
        : "Couldn't read much — please fill in"
      );
    } catch (err) {
      console.error("Receipt scan failed:", err);
      toast.error(err?.response?.data?.detail || "Could not read receipt");
    } finally { setScanning(false); }
  };

  const cur = user?.currency || "USD";
  const currentYm = new Date().toISOString().slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentYm);

  // Rows in the picked month, then category filter.
  const filtered = useMemo(() => {
    return items
      .filter(i => (i.date || "").startsWith(selectedMonth))
      .filter(i => filter === "all" || i.category === filter);
  }, [items, selectedMonth, filter]);

  const monthTotal = filtered.reduce((a, b) => a + Number(b.amount || 0), 0);
  const essentialTotal = filtered.filter(i => i.is_essential).reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div>
      <PageHeader
        subtitle={t("pages.expenses.subtitle")}
        title={t("pages.expenses.title")}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={onReceiptPicked}
              data-testid="receipt-file-input"
            />
            <button
              type="button"
              className="btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              data-testid="scan-receipt-btn"
              title={t("pages.expenses.scan_hint")}
            >
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
              {scanning ? t("pages.expenses.reading") : t("pages.expenses.scan")}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={autoCategorize}
              disabled={categorizing}
              data-testid="auto-categorize-btn"
              title={t("pages.expenses.auto_cat_hint")}
            >
              {categorizing ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {categorizing ? t("pages.expenses.sorting") : t("pages.expenses.auto_cat")}
            </button>
            <button className="btn-primary" onClick={startNew} data-testid="add-expense-btn"><Plus size={16}/> {t("pages.expenses.add")}</button>
          </div>
        }
      />

      <div className="grid md:grid-cols-4 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.expenses.total_entries")} value={filtered.length}/>
        <SummaryStat label={t("pages.expenses.this_month")} value={formatMoney(monthTotal, cur)}/>
        <SummaryStat label={t("pages.expenses.essential")} value={formatMoney(essentialTotal, cur)}/>
        <SummaryStat label={t("pages.expenses.non_essential")} value={formatMoney(monthTotal - essentialTotal, cur)}/>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[var(--text-secondary)]">Month</label>
          <MonthDropdown
            items={items}
            dateKey="date"
            value={selectedMonth}
            onChange={setSelectedMonth}
            testid="expense-month-picker"
          />
        </div>
        <div className="h-6 w-px bg-[var(--border-default)] hidden md:block" />
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter("all")} className={`badge ${filter==="all"?"badge-success":"badge-neutral"}`} data-testid="filter-all">{t("pages.expenses.filter_all")}</button>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setFilter(c)} className={`badge ${filter===c?"badge-success":"badge-neutral"}`} data-testid={`filter-${c}`}>{t(`categories.${c.toLowerCase()}`, { defaultValue: c })}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={t("pages.expenses.empty_title")} body={t("pages.expenses.empty_hint")} action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.expenses.add")}</button>}/>
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
              <tr><Th>{t("common.description")}</Th><Th>{t("common.category")}</Th><Th>{t("pages.expenses.type")}</Th><Th>{t("common.date")}</Th><Th className="text-right">{t("common.amount")}</Th><Th></Th></tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i.id} className="border-t border-[var(--border-default)] hover:bg-[var(--bg-secondary)]/60">
                  <Td><div className="font-medium">{i.description || "-"}</div><div className="text-xs text-[var(--text-muted)]">{i.payment_method}</div></Td>
                  <Td><span className="badge badge-neutral">{t(`categories.${i.category.toLowerCase()}`, { defaultValue: i.category })}</span></Td>
                  <Td>{i.is_essential ? <span className="badge badge-success">{t("pages.expenses.essential")}</span> : <span className="badge badge-warn">{t("pages.expenses.discretionary")}</span>}</Td>
                  <Td className="text-[var(--text-secondary)]">{i.date}</Td>
                  <Td className="text-right num-md">{formatMoney(i.amount, cur)}</Td>
                  <Td className="text-right">
                    <button className="btn-danger-ghost" onClick={() => startEdit(i)}><Pencil size={15}/></button>
                    <button className="btn-danger-ghost" onClick={() => remove(i.id)}><Trash2 size={15}/></button>
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--bg-secondary)] border-t-2 border-[var(--border-default)]">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-[var(--text-primary)]">
                  Total for {new Date(`${selectedMonth}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" })}
                </td>
                <td className="px-4 py-3 text-right num-md text-sm font-semibold text-[var(--text-primary)]" data-testid="expense-month-total">
                  {formatMoney(monthTotal, cur)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => { setOpen(false); setScanBanner(null); }} title={edit ? "Edit expense" : "Add expense"}>
        <form onSubmit={save} className="grid gap-4">
          {scanBanner && (
            <div
              className="flex items-start gap-2.5 p-3 rounded-lg border text-xs"
              style={{
                background: "rgba(224,158,80,0.10)",
                borderColor: "rgba(224,158,80,0.35)",
                color: "var(--text-primary)",
              }}
              data-testid="scan-banner"
            >
              <Sparkles size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#A16A25" }} />
              <div className="leading-relaxed">
                <div className="font-medium">
                  {scanBanner.confidence === "high" ? "Read the receipt with high confidence"
                  : scanBanner.confidence === "medium" ? "Read partially — please review"
                  : "Couldn't read much — please fill in the fields"}
                </div>
                <div className="text-[var(--text-secondary)] mt-0.5">
                  {scanBanner.merchant ? `Merchant: ${scanBanner.merchant}` : "No merchant detected"}
                  {scanBanner.currency && scanBanner.currency !== cur ? ` · Detected currency ${scanBanner.currency} (converted to ${cur} label — verify amount)` : ""}
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Amount (${cur})`}><input type="number" step="0.01" required className="field-input" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="input-expense-amount"/></Field>
            <Field label="Category">
              <select className="field-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="input-expense-category">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description">
            <input
              className="field-input"
              value={form.description}
              onChange={e => setForm({...form, description: e.target.value})}
              onBlur={e => suggestFromDescription(e.target.value)}
              placeholder="Grocery at Whole Foods…"
              data-testid="input-expense-description"
            />
            {suggestedCat && !edit && (
              <div className="mt-1.5 flex items-center gap-2 text-xs" data-testid="expense-cat-suggestion">
                <Sparkles size={12} style={{ color: "#A16A25" }} />
                <span className="text-[var(--text-secondary)]">
                  Usually filed under <span className="font-medium text-[var(--text-primary)]">{suggestedCat.category}</span>
                  {" "}({suggestedCat.count} matches).
                </span>
                <button
                  type="button"
                  className="text-[var(--accent-primary)] font-medium hover:underline"
                  onClick={applySuggestion}
                  data-testid="apply-cat-suggestion-btn"
                >
                  Use it →
                </button>
              </div>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date"><DateField value={form.date} onChange={v => setForm({...form, date: v})} testid="input-expense-date"/></Field>
            <Field label="Payment method">
              <select className="field-input" value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})}>
                {["Card","Cash","Bank Transfer","UPI","Wallet","Other"].map(x => <option key={x}>{x}</option>)}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_essential} onChange={e => setForm({...form, is_essential: e.target.checked})} data-testid="input-expense-essential"/>
            Mark as essential expense
          </label>
          <div className="flex justify-end gap-2 relative">
            <MoneyBurst trigger={burst} tone="neg" />
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" data-testid="submit-expense-btn">{edit ? "Save changes" : "Add expense"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
